export interface AuthenticatedUser {
  oid: string;
  tid: string;
  name: string;
  email: string;
  username: string;
}

export interface MicrosoftAccountProfile {
  localAccountId: string;
  tenantId: string;
  username: string;
  name?: string;
  idTokenClaims?: Record<string, unknown>;
}

function claim(claims: Record<string, unknown> | undefined, key: string): string {
  const value = claims?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

/** Build display identity from MSAL's already-processed ID-token account. */
export function userFromAccount(account: MicrosoftAccountProfile): AuthenticatedUser {
  const username = account.username || claim(account.idTokenClaims, 'preferred_username');
  const email = claim(account.idTokenClaims, 'email') || username;
  return {
    oid: claim(account.idTokenClaims, 'oid') || account.localAccountId,
    tid: claim(account.idTokenClaims, 'tid') || account.tenantId,
    name: account.name || claim(account.idTokenClaims, 'name') || email || 'Microsoft user',
    email,
    username,
  };
}

/**
 * Two-letter initials for the header avatar.
 *
 * Entra names arrive in two shapes: a real display name ("Ada Lovelace") and,
 * when the directory has none, the email itself. Both have to produce something
 * readable, so a name uses its first and last word while an email falls back to
 * the local part split on its separators - `ada.lovelace@x.com` is `AL`, not
 * `AD`.
 */
export function userInitials(user: Pick<AuthenticatedUser, 'name' | 'email'>): string {
  const source = user.name.trim() || user.email.trim();
  if (!source) return '?';
  const local = source.split('@')[0] ?? source;
  const words = local
    .split(/[\s._\-+]+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  const head = words[0];
  if (!head) return '?';
  const tail = words.length > 1 ? words[words.length - 1] : undefined;
  const second = tail ? tail.slice(0, 1) : head.slice(1, 2);
  return (head.slice(0, 1) + second).toUpperCase();
}
